"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { CustomBreadcrumbs } from "ui"
import Container from "ui/Container"
import { extractProperty, getBreadCrumbs, replaceRichTextLinks } from "utils"
import ReactPlayerComponent from "../Homepage/Hero/ReactPlayer"
import { formatDate } from "../PeopleOfEgged/ArticlesGrid"

const StyledLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  gap: 32px;
  padding-bottom: 64px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 800px;
    margin-inline: auto;
    gap: 48px;
    padding-top: 48px;
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledTitle = styled(Typography)`
  font-size: 32px;
  line-height: 40px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 40px;
    line-height: 48px;
  }
`

const DescriptionText = styled(Typography)``

const DetailsBox = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-top: 1px solid #f5f6f8;
  border-bottom: 1px solid #f5f6f8;
`

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const MediaContainer = styled.div`
  width: 100%;
  aspect-ratio: 328 / 230;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  ${({ theme }) => theme.breakpoints.up("md")} {
    aspect-ratio: 800 / 400;
  }
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`
interface ArticleContentItem {
  media: string
  content: string
  subTitle: string
  alt: string
}

export default function ContentPage({
  data,
  locale,
}: {
  data: any
  locale: string
}) {
  const page = data?.page
  const articleTitle = extractProperty(page, "articleTitle")
  const articleShortDescription = extractProperty(
    page,
    "articleShortDescription",
  )
  const articleDate = extractProperty(page, "articleDate")
  const articleAuthor = extractProperty(page, "articleAuthor")
  const articleContent = extractProperty(page, "articleContent") || []
  const items = articleContent.map((item: any) => {
    return {
      media: extractProperty(item, "media")?.[0]?.url,
      alt: extractProperty(item, "media")?.[0]?.name,
      content: extractProperty(item, "content"),
      subTitle: extractProperty(item, "subTitle"),
    } as ArticleContentItem
  })
  const breadCrumbs = getBreadCrumbs(data, locale)
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, "")
  }
  return (
    <Container paddingMobile={true}>
      <StyledLayout>
        <CustomBreadcrumbs breadcrumbs={breadCrumbs} noPadding />
        <ContentLayout>
          <TextBox>
            <StyledTitle variant="h1">{articleTitle}</StyledTitle>
            <DescriptionText variant="body1">
              {articleShortDescription}
            </DescriptionText>
          </TextBox>
          <DetailsBox>
            <Typography variant="body2">
              עודכן בתאריך {formatDate(articleDate)}
            </Typography>
          </DetailsBox>

          {items.map((item: ArticleContentItem, index: number) => (
            <ContentBox key={index}>
              {item.media && (
                <MediaContainer>
                  <ReactPlayerComponent
                    source={item.media}
                    playing={false}
                    volume={false}
                    setPlaying={() => {}}
                    setVolume={() => {}}
                    alt={item.alt}
                  />
                </MediaContainer>
              )}

              {item.subTitle && (
                <Typography variant="h2">{item.subTitle}</Typography>
              )}
              {item.content && (
                <StyledTypography
                  variant="body1"
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(item.content),
                  }}
                />
              )}
            </ContentBox>
          ))}
        </ContentLayout>
      </StyledLayout>
    </Container>
  )
}
